import React, {useState} from 'react';
import {GitMerge, Plus, Loader2} from 'lucide-react';
import {Button} from './ui/Button';
import {Field} from './ui/Input';

interface OnboardingProps {
    onCreateOrganization: (name: string) => Promise<void>;
    onSkip?: () => void;
}

export const Onboarding: React.FC<OnboardingProps> = ({onCreateOrganization, onSkip}) => {
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!inputValue.trim() || isLoading) return;
        setIsLoading(true);
        try {
            await onCreateOrganization(inputValue);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-[#f4f1e6] p-4 text-[#0a0a0a]">
            <div className="w-full max-w-xl">
                <div className="mb-10 text-center">
                    <div className="mb-4 inline-flex items-center gap-3">
                        <div className="nb-shadow-sm rounded-2xl border-2 border-[#0a0a0a] bg-[#22c55e] p-3">
                            <GitMerge size={32} className="text-[#0a0a0a]" strokeWidth={2.6}/>
                        </div>
                        <h1 className="text-4xl font-extrabold tracking-tight md:text-5xl">ReviewMe</h1>
                    </div>
                    <p className="text-xl font-medium text-[#4f4f48]">Welcome to your automation HQ.</p>
                </div>

                <div className="nb-shadow-lg relative mx-auto rounded-2xl border-2 border-[#0a0a0a] bg-white p-8 md:p-10">
                    <h2 className="mb-6 text-center text-2xl font-extrabold">Create your Organization</h2>
                    <p className="mb-6 text-center font-medium text-[#4f4f48]">
                        To get started, create a new organization. You can invite team members later
                        from the settings.
                    </p>

                    <form onSubmit={handleSubmit}>
                        <div className="mb-6">
                            <Field
                                label="Organization Name"
                                type="text"
                                autoFocus
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                placeholder="e.g. Acme Engineering"
                                className="text-lg"
                            />
                        </div>
                        <Button type="submit" disabled={!inputValue.trim() || isLoading} className="w-full text-base">
                            {isLoading ? (
                                <><Loader2 size={20} strokeWidth={3} className="animate-spin"/> Creating...</>
                            ) : (
                                <><Plus size={20} strokeWidth={3}/> Create Workspace</>
                            )}
                        </Button>
                        {onSkip && (
                            <button
                                type="button"
                                onClick={onSkip}
                                className="mt-4 w-full py-2 font-bold text-[#4f4f48] transition-colors hover:text-[#0a0a0a]"
                            >
                                Skip for now
                            </button>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
};
